import { replace, split, get, has } from 'lodash';
import NumberUtils from '../../utils/NumberUtils';
import AccountsStore from '../../stores/AccountsStore';
import { isVMBusiness } from '../../utils/BrandUtils';
import { accountHasLimitedRead } from '../../utils/AccountCapabilityUtils';
import StringConstants from '../../constants/StringConstants';

class HomepageBL {
  getAccountDisplayNumber(accountDetails) {
    if (accountDetails.type === 'credit_card') {
      let creditcardNumber = replace(
        accountDetails.number,
        '************',
        'xxxxxxxxxxxx'
      );
      creditcardNumber = split(creditcardNumber, '-');
      const creditcard = NumberUtils.formatCreditCard(creditcardNumber[0]);
      return ` ${creditcard}`;
    } else {
      const accountNumber = split(accountDetails.number, '-');
      const sortcode = NumberUtils.formatSortCode(accountNumber[0]);
      return {
        sortcode,
        accountNumber: accountNumber[1],
      };
    }
  }

  getBalances(account) {
    const getValue = (amount, currency) =>
      NumberUtils.appendCurrency('{}', amount, currency);
    if (accountHasLimitedRead(account)) return {};
    return account.balances.reduce((data, { amount, currency, type }) => {
      switch (type) {
        case 'current':
          return {
            ...data,
            currentBalance: getValue(amount, currency),
          };

        case 'available':
          return {
            ...data,
            availableBalance: getValue(amount, currency),
          };

        default:
          return data;
      }
    }, {});
  }

  getAdverts(ad) {
    switch (ad) {
      case 'savingspot':
        return {
          id: 'pots',
          heading: 'Set up pots, save up lots',
          text: 'Put away whatever works for you. Every penny gets you closer to your goals.',
          linkName: 'Manage your savings pots',
          image: {
            VM: '../images/vm/green-slab-header.png',
            others: '../images/bow/bfeature_savings-pot.png',
          },
        };
      case 'alerts':
        return {
          id: 'alerts',
          heading: 'Stay one step ahead',
          text: 'Keep up-to-date with what’s in your account with regular alerts.',
          linkName: 'Manage your alerts',
          image: {
            B: '../images/bow/bfeature_alerts.png',
            VM: isVMBusiness()
              ? '../images/vm/vm-pattern-midnight-blue.png'
              : '../images/vm/pink-slab-header.png',
            others: '../images/bow/CYB_Alert.jpg',
          },
        };
      case 'spending':
        return {
          id: 'spending',
          heading: 'Take control',
          text: 'Control your money by setting budgets and tracking your spending.',
          linkName: 'Manage your spending',
          image: {
            VM: '../images/vm/purple-slab-header.png',
            others: '../images/bow/bfeature_spending.png',
          },
        };
      case 'manageaccountpools':
        return {
          id: 'manageaccountpools',
          heading: 'Accounts pools',
          text: 'See the combined balances and transactions of more than one business account at a glance',
          linkName: 'Show account pools',
          image: {
            VM: '../images/vm/vm-pattern-midnight-blue.png',
            others: '',
          },
        };
      case 'marketplace':
        return {
          id: 'marketplace',
          heading: 'Products and services',
          text: 'To help your business grow, discover a range of tools, products and services from Virgin Money and partners.',
          linkName: 'Go to Marketplace',
          image: {
            VM: '../images/vm/marketplace/marketplace-tile-header.png',
            others: '',
          },
        };
      case 'vulnerable-customers':
        return {
          id: 'vulnerable-customers',
          secondaryLinkId: 'brochureware-your-support',
          heading: 'Your Support',
          text: 'Share any health concerns, money worries or accessibility needs you have to get extra support.',
          linkName: 'Your Support',
          secondaryLinkName: 'Find out more',
          image: {
            VM: '../images/vm/vulnerable-customers/vulnerable-customers.png',
            others: '',
          },
          opensInANewTab: true,
        };
      default:
        return {};
    }
  }

  setPageForAlerts() {
    const accountDetails = [];
    AccountsStore.getAccountData().map((accounts) => {
      const isAlertsAvailable =
        accounts.actionsAvailable['/account/alerts'] ||
        accounts.actionsAvailable['/account/alerts/transaction/debit'] ||
        accounts.actionsAvailable['/account/alerts/transaction/credit'];
      if (isAlertsAvailable) {
        accountDetails.push({ id: accounts.id });
        return accounts;
      }
    });
    if (accountDetails.length > 1) {
      return { pageType: 'alertsAccounts', param: 'multiple' };
    } else {
      return {
        pageType: 'alertsList',
        id: accountDetails[0].id,
        param: 'single',
      };
    }
  }

  shouldShowAvailableBalance(account) {
    const balances = this.getBalances(account);
    if (
      account.type === 'credit_card' ||
      account.product.code === StringConstants.KAPITI_PRODUCT_CODE
    ) {
      return get(balances, 'availableBalance');
    }
    return (
      get(account.actionsAvailable, '/account/balance/available') &&
      has(balances, 'availableBalance')
    );
  }
}

export default new HomepageBL();
